import objectPath from 'object-path';
import {
  observable,
  action,
  extendObservable,
  isComputedProp,
  isObservable,
  getAtom,
  reaction,
} from 'mobx';
import { client } from 'core/client';
import { unObserve } from 'core/utils';

import { handleGraphqlErrors, reportRequestError } from './errors';

export class Variables {
  disposers = [];
  props = [];

  constructor(obj = {}) {
    Object.keys(obj).forEach(key => {
      this.props.push(key);
      if (typeof obj[key] === 'function') {
        Object.defineProperty(obj, key, { get: obj[key] });
      }
    });
    extendObservable(this, obj);
  }

  @action extend = newVars => {
    const newObj = {};
    this.props.forEach(prop => {
      if (isComputedProp(this, prop)) {
        newObj[prop] = getAtom(this, prop).derivation;
      }
    });
    newVars.props.forEach(prop => {
      if (isComputedProp(newVars, prop)) {
        newObj[prop] = getAtom(newVars, prop).derivation;
      }
    });
    return new Variables(newObj);
  };

  @action dispose = () => {
    this.disposers.forEach(disposer => disposer());
  };

  @action onChange = (varNames, handler, { fireImmediately = true } = {}) => {
    this.disposers.push(
      reaction(
        () => {
          const newVars = {};
          varNames.forEach(varName => {
            const varSegs = varName.split('.');
            let target = this;
            varSegs.forEach(seg => {
              newVars[seg] = target[seg];
              target = target[seg];
            });
          });
          return newVars;
        },
        newVars => {
          handler(newVars);
        },
        { name: `On vars changed ${varNames}`, fireImmediately },
      ),
    );
  };
}

// Similar to objectPath.get but works on observables and pathSegs is already a segments array
export const resultFromPathSegments = (res, pathSegs) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const path of pathSegs) {
    res = res[path];
    // Another option would be using res == null (two equals),
    // but could be less explicit and prone to be modified back to === in the future.
    if ([null, undefined].includes(res)) return null;
  }
  return res;
};

export class Processor {
  @observable result = null;
  @observable intermediateResult = null;

  constructor(query, processorFunc, callback, path = null) {
    // query.result is already the sub path of the raw result (using query.constructor.conf.resultPath)
    this.query = query;
    const pathSegments = [];
    if (path !== null) {
      pathSegments.push(...path.split('.'));
    }
    this.pathSegments = pathSegments;
    this.processorFunc = processorFunc;
    this.callback = callback;
    this.initial = true;
  }

  run(networkData) {
    // set as observable because networkData is apollo cache which is read-only
    // and some parts of our system do try to directly modify it. This way they
    // modify the observable copy.
    this.result = networkData;
    const resultPart = resultFromPathSegments(this.result, this.pathSegments);

    const intermediateResult = this.processorFunc(resultPart, networkData);

    // The point here is casting processedResult into an observable
    // This is a (bad) requirement in our codebase so that models detect
    // the data being loaded is observable to go through their logic.
    this.intermediateResult = intermediateResult;

    // if (processedResult instanceof App) {
    //   if (!processedResult.manager) {
    //     processedResult.setManager(this.query.appManager);
    //   }
    // }

    if (this.callback) {
      this.callback(this.intermediateResult, this.query);
    }
  }
}

// FIXME - resultPath is a required parameter, check for it on constructor
export class Query {
  @observable loading = true;
  @observable updating = false;
  @observable result = null;
  @observable userErrors = null;
  @observable sysError = null;
  @observable notFound = false;
  processors = observable.map();
  onSysErr = null;
  disposers = [];
  queryStack = undefined;
  evaluatedVariables = {};

  constructor(
    appManager,
    {
      queryKey = null,
      onUserErr = null,
      onSysErr = null,
      fetchPolicy = 'cache-and-network',
      pollInterval = undefined,
      variables = null,
      headers = null,
      abortOnRerequest = false,
    } = {},
  ) {
    this.setOnSysError(onSysErr);
    this.setOnUserError(onUserErr);
    this.errored = false;
    this.appManager = appManager;
    this.queryKey = queryKey || this.appManager.queries.size;
    this.variables = variables || this.appManager.variables;
    this.fetchPolicy = fetchPolicy;
    this.pollInterval = pollInterval;
    this.abortOnRerequest = abortOnRerequest;
    this.headers = headers;
    this.createQuery();
    appManager.queries.set(this.queryKey, this);
  }

  createQuery = () => {
    let context = {};
    if (this.abortOnRerequest) {
      context = {
        abortOnRerequest: true,
        queryDeduplication: false,
      };
    }
    if (this.headers) {
      context.headers = this.headers;
    }

    this.evaluatedVariables = isObservable(this.variables)
      ? unObserve(this.variables, this.constructor.conf.varNames)
      : this.variables;
    this.observableQuery = client.watchQuery({
      query: this.constructor.conf.GQL,
      variables: this.evaluatedVariables,
      fetchPolicy: this.fetchPolicy,
      pollInterval: this.pollInterval,
      context,
    });
  };

  query = () => {
    if (window.location.hostname !== 'wikifactory.com') {
      this.queryStack = new Error().stack;
    }

    this.subscription = this.observableQuery.subscribe({
      next: this.Update,
      error: this.OnError,
      // complete: () => {},
    });
  };

  cancel = () => {
    this.subscription.unsubscribe();
    this.errored = true;
  };

  process(processor, callback, { subPath = null, processorKey = null } = {}) {
    if (processorKey === null) {
      processorKey = this.processors.size;
    }
    this.processors.set(
      processorKey,
      new Processor(this, processor, callback, subPath),
    );
  }

  @action Update = result => {
    this.queryStack = null;
    this.sysError = null;
    this.notFound = false;
    const { resultPath } = this.constructor.conf;

    // it is reasonable but should be unnecessary
    // because default apollo policy should prevent from
    // Updating data when it's just partial data
    // if (result?.partial) return;

    if (resultPath && !objectPath.has(result.data, resultPath)) {
      console.error(
        `Trying to get resultPath '${resultPath}' on Query ${this.constructor.name} which does not exist on the result`,
      );
    }

    this.result = objectPath.get(result.data, resultPath, null);
    if (!result.loading || result.data !== undefined) {
      this.loading = false;
    }

    const isCacheHit = result.data !== undefined && result.loading;
    if (isCacheHit) {
      // We have a cache hit - we don't want to go into loading mode.
      // However, we may want to indicate that we are updating
      this.updating = true;
    } else {
      this.updating = false;
    }

    this.processors.forEach(processor => {
      processor.run(objectPath.get(result.data, resultPath, null));
    });
  };

  @action OnError = err => {
    const [sysErr, userErrs] = handleGraphqlErrors(this, err);

    this.loading = this.observableQuery.getCurrentResult().loading;
    // Hack around bug in apollo which doesn't allow refetching after an error is thrown
    // https://github.com/apollographql/apollo-client/issues/8833
    this.errored = true;

    if (userErrs.length !== 0) {
      // If there are in fact user errors (graphqlErrors which are expected and therefore not system errors)
      this.userErrors = userErrs;
      if (this.onUserErr) {
        this.onUserErr(userErrs[0]);
      }
    } else if (sysErr) {
      this.sysError = sysErr;

      if (this.onSysErr) {
        this.onSysErr(sysErr);
      }

      reportRequestError(sysErr, this);
    }
  };

  setOnSysError(callback) {
    this.onSysErr = callback;
  }

  setOnUserError(callback) {
    this.onUserErr = callback;
  }

  refetchQuery = (onComplete = () => true) => {
    this.loading = true;
    this.notFound = false;
    // Hack around bug in apollo which doesn't allow refetching after and error is thrown
    if (this.errored) {
      this.errored = false;
      this.disposer();
      this.subscription = null;
      this.createQuery();
      this.query();
      return;
    }
    const refetchPromise = this.observableQuery.refetch(
      isObservable(this.variables)
        ? unObserve(this.variables, this.constructor.conf.varNames)
        : this.variables,
    );
    refetchPromise.then(result => {
      // When you refetch queries with different variables and they return the same result...
      // The observableQuery next/error/complete callback are never called.
      // We use this promise to ensure loading is set to false after the refetch completes.
      this.loading = this.observableQuery.getCurrentResult().loading;
      onComplete(result);
      return result;
    });
  };

  disposer() {
    // unnecssary all reactions are on processors
    // eslint-disable-next-line no-restricted-syntax
    for (const dispose of this.disposers) {
      dispose();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
